import request from '~/api/request';

const { VITE_NOESIS_URL_ROOT } = process.env;

// Types based on the actual SSE server implementation
export interface MessageRequest {
  content: string;
}

export interface ThreadMessage {
  id: string;
  type: 'human' | 'ai';
  content: string;
  timestamp: string;
}

export interface Thread {
  thread_id: string;
  name: string;
  user_id: string;
  created_at: string;
  messages: ThreadMessage[];
  total_tokens?: number;
  token_limit?: number;
}

export interface HealthResponse {
  status: string;
}

export interface AssistantEvent {
  type:
    | 'status'
    | 'error'
    | 'complete'
    | 'connection'
    | 'keepalive'
    | 'start'
    | 'tool_update'
    | 'assistant_delta'
    | 'assistant_final'
    | 'message_confirmed'
    | 'transcript'
    | 'pong'
    | 'response'
    | 'thread_name_updated'
    | 'stopped'
    | 'cancelled'
    | 'token_usage'
    | 'thread_limit_reached'
    | 'entity_changed';
  data: Record<string, any>;
  timestamp: string;
  thread_id?: string;
}

export const getWsUrl = (threadId: string, token: string): string => {
  if (!VITE_NOESIS_URL_ROOT) {
    throw new Error('VITE_NOESIS_URL_ROOT environment variable is required');
  }
  // Convert http(s) base URL to ws(s) for the WebSocket endpoint.
  const base = VITE_NOESIS_URL_ROOT.replace(/^https/, 'wss').replace(/^http/, 'ws');
  return `${base}threads/${threadId}/ws?token=${token}`;
};

/** Page Noesis was opened from; it only picks which suggestions to show */
export type NoesisContext = 'general' | 'reports' | 'alerts' | 'dashboards';

export const createThread = (data: { name: string; user_id: string; organization_id: string; metadata: Record<string, any> }): Promise<Thread> => {
  return request('post', '/threads/', data, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<Thread>;
};

export const fetchThreads = (params?: { report_id?: string }): Promise<Thread[]> => {
  return request('get', '/threads/', params || null, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<Thread[]>;
};

export const fetchThread = (id: string): Promise<Thread[]> => {
  return request('get', `/threads/${id}`, null, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<Thread[]>;
};

export const deleteThread = (id: string): Promise<any> => {
  return request('delete', `/threads/${id}`, null, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<any>;
};

export const updateThread = (id: string, data: { name?: string; pinned?: boolean }): Promise<Thread> => {
  return request('patch', `/threads/${id}`, data, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<Thread>;
};

export const fetchSuggestions = (params: { organization_id?: string; time_zone?: string; context?: NoesisContext }) => {
  return request('post', `/suggestions`, params, { baseURL: VITE_NOESIS_URL_ROOT });
};

export const sendFeedback = (params: { threadId: string; messageId: string; runId: string; feedback: 'good' | 'bad' }) => {
  const { threadId, messageId, runId, feedback } = params;
  return request('post', `/threads/${threadId}/messages/${messageId}/feedback`, { run_id: runId, feedback }, { baseURL: VITE_NOESIS_URL_ROOT });
};

export const transcribeVoiceMessage = (audioBase64: string, mimeType: string) => {
  return request('post', '/threads/transcribe', { audio: audioBase64, mime_type: mimeType }, { baseURL: VITE_NOESIS_URL_ROOT });
};

// External data files a user attaches to a thread for analysis (e.g. weather-station data)
export interface AttachedFile {
  file_id: string;
  filename: string;
}

export interface ThreadFilesResponse {
  thread_id: string;
  file?: AttachedFile;
  files: AttachedFile[];
}

export const uploadThreadFile = (threadId: string, file: File): Promise<{ data: ThreadFilesResponse }> => {
  const formData = new FormData();
  formData.append('file', file);
  return request('post', `/threads/${threadId}/files`, formData, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<{ data: ThreadFilesResponse }>;
};

export const listThreadFiles = (threadId: string): Promise<{ data: ThreadFilesResponse }> => {
  return request('get', `/threads/${threadId}/files`, null, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<{ data: ThreadFilesResponse }>;
};

export const deleteThreadFile = (threadId: string, fileId: string): Promise<{ data: ThreadFilesResponse }> => {
  return request('delete', `/threads/${threadId}/files/${fileId}`, null, { baseURL: VITE_NOESIS_URL_ROOT }) as Promise<{ data: ThreadFilesResponse }>;
};
